import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { ConsumptionRow } from "../models/consumptionRow";
import { EnergyDataStatus } from "../models/energyDataQuality";
import { energyHintTexts, energyIssueTexts } from "../utilities/energyIssueTexts";

const ICONS: Record<EnergyDataStatus, JSX.Element> = {
  ok: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  partial: <WarningAmberIcon fontSize="small" color="warning" />,
  missing: <HelpOutlineIcon fontSize="small" color="warning" />,
  failed: <ErrorOutlineIcon fontSize="small" color="error" />,
  pending: <HourglassEmptyIcon fontSize="small" color="disabled" />,
};

/**
 * Says how far the figures on a row can be trusted, and why.
 *
 * The report exists to be checked against reality, so a vehicle Geotab answered incompletely for
 * has to say so on its own row. The label alone would not be enough to act on: the tooltip names
 * each gap and, where the row knows it, its size.
 */
export function DataQualityCell({ row }: { row: ConsumptionRow }) {
  const { t, i18n } = useTranslation();

  const label = useStatusLabel(row.status);
  const issues = energyIssueTexts(row, t, i18n.language);
  const hints = energyHintTexts(row, t, i18n.language);
  const lines = [...issues, ...hints];

  return (
    <Tooltip
      title={
        lines.length > 0 ? (
          <Stack spacing={0.5}>
            {lines.map(line => (
              <Typography key={line} variant="caption">
                {line}
              </Typography>
            ))}
          </Stack>
        ) : (
          label
        )
      }
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
        {ICONS[row.status]}
        {/* A figure can be complete and still implausible. Without a mark of its own, a flagged
            fill-up deviation would sit behind a row labelled "complete" and never be hovered. */}
        {hints.length > 0 && <PriorityHighIcon fontSize="small" color="warning" />}
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function useStatusLabel(status: EnergyDataStatus) {
  const { t } = useTranslation();

  switch (status) {
    case "ok":
      return t("energyReport.quality.ok", "Vollständig");
    case "partial":
      return t("energyReport.quality.partial", "Unvollständig");
    case "missing":
      return t("energyReport.quality.missing", "Keine Messwerte");
    case "failed":
      return t("energyReport.quality.failed", "Abfrage fehlgeschlagen");
    case "pending":
      return t("energyReport.quality.pending", "Wird geladen");
  }
}
